import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { find, isEmpty, values } from "lodash";
import Tooltip from "rc-tooltip";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import shallow from "zustand/shallow";
import { commissionTypeOptions } from ".";
import Checkbox from "../../../../components/forms/Checkbox";
import DropdownSelect, {
  DropdownSelectOption
} from "../../../../components/forms/DropdownSelect";
import FormProvider from "../../../../components/forms/FormProvider";
import Input from "../../../../components/forms/Input";
import InfoIcon from "../../../../components/icons/InfoIcon";
import { useCommissionTemplates } from "../../../../context/commissionTemplates";
import {
  frequencyOptions,
  getCronExpressionFromFrequency
} from "../../../../context/installs/InstallCommissionFormSidebar";
import {
  CommissionFrequency,
  CommissionTemplate,
  CommissionType
} from "../../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../../utils/useCurrency";

export interface Props {
  commission: CommissionTemplate | undefined;
  open: boolean;
  toggleEditCommissionForm: (
    commission: CommissionTemplate | undefined
  ) => void;
}

const CommissionTemplateFormSidebar: React.FC<Props> = ({
  commission,
  open,
  toggleEditCommissionForm
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    commissionTemplates,
    addCommissionTemplate,
    updateCommissionTemplate
  } = useCommissionTemplates();
  const [currencyOptions, getCurrencyTitle] = useCurrency(
    (state) => [state.currencyOptions, state.getCurrencyTitle],
    shallow
  );

  const closeSidebar = useCallback(
    () => toggleEditCommissionForm(undefined),
    [toggleEditCommissionForm]
  );

  const initialValues = useMemo(
    () =>
      commission
        ? {
            name: commission.name,
            type: find(commissionTypeOptions, { key: commission.type }),
            frequency: frequencyOptions[CommissionFrequency.monthly],
            value: commission.value,
            currency: find(currencyOptions, { key: commission.currency }),
            primary: commission.primary
          }
        : {
            name: "",
            type: find(commissionTypeOptions, {
              key: CommissionType.percentage
            }),
            frequency: frequencyOptions[CommissionFrequency.monthly],
            currency: find(currencyOptions, { key: CurrencyKey.hnt }),
            primary: isEmpty(commissionTemplates)
          },
    [commission, commissionTemplates, currencyOptions]
  );

  const onSubmit = useCallback(
    async (
      { name, currency, value, type, frequency, primary }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const cronExpression = getCronExpressionFromFrequency(
        [CommissionType.recurringFixed, CommissionType.oneTimeFixed].includes(
          type.key
        )
          ? frequency.key
          : null
      );

      const data = {
        name,
        type: type.key as CommissionType,
        cron_expression: cronExpression,
        currency: currency.key,
        value,
        primary
      };

      let apiResponse;

      if (!!commission) {
        apiResponse = await updateCommissionTemplate(commission.uuid, data);
      } else {
        apiResponse = await addCommissionTemplate(data);
      }

      if (apiResponse.status) {
        closeSidebar();
      } else {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [commission, updateCommissionTemplate, addCommissionTemplate, closeSidebar]
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={closeSidebar}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <FormProvider
                  classnames={{ wrapper: "h-full" }}
                  onCancel={closeSidebar}
                  error={errorMessage}
                  formikProps={{
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape({
                      name: Yup.string().required("Required"),
                      type: Yup.object().required("Required"),
                      currency: Yup.object().required("Required"),
                      frequency: Yup.object().required("Required"),
                      value: Yup.number().required("Required")
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    values: formValues
                  }: FormikProps<FormValues>) => {
                    let valueLabel = "Value";
                    let valuePlaceholder = "Enter a value";

                    if (formValues.type?.key === CommissionType.percentage) {
                      valueLabel = "Percentage";
                      valuePlaceholder = "Enter a value between 1 and 100";
                    }

                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              {!!commission ? "Edit" : "New"} commission
                              template
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={closeSidebar}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              <Field
                                id="name"
                                name="name"
                                label="Name"
                                placeholder="Template name"
                                type="text"
                                component={Input}
                                disabled={isSubmitting}
                              />

                              <Field
                                label="Type"
                                name="type"
                                placeholder="Select a type"
                                disabled={isSubmitting}
                                items={commissionTypeOptions}
                                itemToString={(
                                  item: DropdownSelectOption<CommissionType>
                                ) => item.title}
                                optionToString={(
                                  item: DropdownSelectOption<CommissionType>
                                ) => item.title}
                                component={DropdownSelect}
                              />

                              {formValues.type?.key ===
                                CommissionType.recurringFixed && (
                                <Field
                                  label="Frequency"
                                  name="frequency"
                                  disabled
                                  items={values(frequencyOptions)}
                                  itemToString={(
                                    item: DropdownSelectOption<CommissionFrequency>
                                  ) => item.title}
                                  optionToString={(
                                    item: DropdownSelectOption<CommissionFrequency>
                                  ) => item.title}
                                  component={DropdownSelect}
                                />
                              )}

                              <Field
                                label="Currency"
                                name="currency"
                                placeholder="Select a currency"
                                disabled={isSubmitting}
                                items={currencyOptions}
                                itemToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                optionToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                component={DropdownSelect}
                              />

                              <Field
                                id="value"
                                name="value"
                                label={valueLabel}
                                placeholder={valuePlaceholder}
                                type="number"
                                component={Input}
                                disabled={isSubmitting}
                                validate={(value: number) => {
                                  if (
                                    formValues.type.key ===
                                      CommissionType.percentage &&
                                    (value < 1 || value > 100)
                                  ) {
                                    return "Value must be between 1 and 100.";
                                  } else if (value < 1) {
                                    return "The value field is required.";
                                  }
                                }}
                              />

                              <Field
                                label={
                                  <div className="inline-flex items-center">
                                    <div>Use as default template</div>
                                    <Tooltip
                                      placement="top"
                                      overlay={`The "default" commission template will be automatically applied to linked location contacts for new installs.`}
                                    >
                                      <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                                    </Tooltip>
                                  </div>
                                }
                                name="primary"
                                disabled={isSubmitting}
                                type="checkbox"
                                component={Checkbox}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CommissionTemplateFormSidebar;

//
// Utils
//

interface FormValues {
  name: string;
  type: DropdownSelectOption<CommissionType>;
  currency: DropdownSelectOption<CurrencyKey>;
  value: string;
  primary: boolean;
  frequency: DropdownSelectOption<CommissionFrequency>;
}

const ORDERED_FIELD_NAMES = [
  "name",
  "currency",
  "type",
  "frequency",
  "value",
  "primary"
];
